<script setup lang="tsx">
import PlusButton from '@/components/widgets/PlusButton.vue'
import type { StandardRuleMetaData } from '@/models/api-generated'
import methodEditor from '@/stores/method-editor'
import { computed, onBeforeMount, ref, watch } from 'vue'
import RuleStatusIcon from '@/components/widgets/RuleStatusIcon.vue'
import RuleStatusSelect from '@/components/widgets/RuleStatusSelect.vue'
import SubRuleRow from './SubRuleRow.vue'
import { type RuleStatus } from '@/models/enums-generated'
import { calculateParentRuleStatus, isCommentRequired } from '@/services/utils'

const props = defineProps({
	isReadOnly: Boolean,
})
const model = defineModel<StandardRuleMetaData>({ required: true })
const isExpanded = ref(false)
const editor = methodEditor()
const isEditMode = computed(() => !props.isReadOnly && editor.isEditMode)

const isParentChangeEvent = ref(false)

const hasSubrules = computed(() => model.value.sub_rules.length > 0)

const annotationLabels = ['Selgitus:', 'Vajaduse põhjendus:', 'NIS2:']

const ruleParts = computed(() => {
	const value = model.value.rule.value
	const matches: { label: string; idx: number }[] = []
	for (const label of annotationLabels) {
		const sep = `\n\n${label}`
		let from = 0
		while (true) {
			const i = value.indexOf(sep, from)
			if (i < 0) break
			matches.push({ label, idx: i })
			from = i + sep.length
		}
	}
	matches.sort((a, b) => a.idx - b.idx)

	const body = matches.length === 0 ? value : value.slice(0, matches[0].idx)
	const annotations = matches.map((m, k) => {
		const start = m.idx + 2 + m.label.length
		const end = k + 1 < matches.length ? matches[k + 1].idx : value.length
		return { label: m.label, text: value.slice(start, end).trim() }
	})
	const isListPrefix = body.length >= 2 && body[1] === '.'
	return { body, isListPrefix, annotations }
})

onBeforeMount(async () => {
	if (hasSubrules.value) {
		isExpanded.value = true

		watch(
			() => model.value.sub_rules.map((subrule) => subrule.status),
			(newStatuses) => {
				setTimeout(() => {
					if (!isParentChangeEvent.value) {
						model.value.status = calculateParentRuleStatus(newStatuses)
					}

					isParentChangeEvent.value = false
				}, 0)
			},
		)

		watch(
			() => model.value.status,
			(status: RuleStatus) => {
				setTimeout(() => {
					if (isParentChangeEvent.value) {
						model.value.sub_rules.forEach((subrule) => {
							subrule.status = status
						})
					}
				}, 0)
			},
		)
	}
})
</script>

<template>
	<div class="row">
		<div class="col d-inline-flex align-items-center">
			<div class="col ms-3">
				<div class="mb-3" style="white-space: pre-wrap">
					<template v-if="ruleParts.isListPrefix">
						<strong>{{ ruleParts.body.slice(0, 1) }}</strong
						>{{ ruleParts.body.slice(1) }}
					</template>
					<template v-else>{{ ruleParts.body }}</template>
					<div v-for="a in ruleParts.annotations" :key="a.label" class="small mt-2">
						<strong>{{ a.label }}</strong> {{ a.text }}
					</div>
				</div>

				<div v-if="model.comment || isEditMode" class="row ms-1">
					<div class="col">
						<div v-if="!isEditMode" class="d-flex">
							<i class="bi bi-chat-right-text text-primary"></i>

							<div class="d-flex">
								<div class="border-start border-2 border-secondary opacity-25 me-2 ms-2"></div>
								<div class="text-break small">
									{{ model.comment }}
								</div>
							</div>
						</div>
						<textarea
							v-else
							:id="'method-comment' + model.id"
							class="form-control mt-1"
							:class="{ 'is-invalid': isCommentRequired(model.status, model.comment) }"
							maxlength="500"
							v-model="model.comment"
							:placeholder="$t('misc.comment')"
							:disabled="props.isReadOnly"
						></textarea>
					</div>
				</div>
				<div v-if="hasSubrules">
					<PlusButton v-model="isExpanded"></PlusButton>
				</div>
			</div>
		</div>

		<div class="col-4 align-self-center text-center">
			<RuleStatusIcon v-if="!isEditMode" v-model="model.status"></RuleStatusIcon>
			<RuleStatusSelect
				v-else
				:id="'rule-status-select' + model.id"
				v-model="model.status"
				v-model:isParentChangeEvent="isParentChangeEvent"
				:has-subrules="hasSubrules"
				class="d-inline"
				:disabled="props.isReadOnly"
			></RuleStatusSelect>
		</div>

		<div v-if="isExpanded" class="ps-4">
			<div class="subrules rounded px-2">
				<SubRuleRow v-model="model" :is-read-only="props.isReadOnly"></SubRuleRow>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.subrules
	background-color: var(--bs-secondary-bg)
</style>
